import * as React from 'react';
import { NextPage } from 'next';
import { Dispatch } from 'redux';
import Head from 'next/head';
import { useRouter } from 'next/router';
import * as url from 'url';

import { wrapper } from 'ducks';
import { fetchConfigForHostname } from 'lib/util/fetchConfigForHostname';
import { ApiKeyContext } from 'contexts/ApiKeyContext';
import { CurrentUrlContext } from 'contexts/CurrentUrlContext';
import { BookingWidgetProductDetails } from 'components/BookingWidgetProductDetails/BookingWidgetProductDetails';
import { BookingWidgetLayout } from 'components/BookingWidgetLayout/BookingWidgetLayout';
import {
  fetchProductServerSide,
  fetchSettingsServerSide,
  fetchCustomPagesServerSide,
} from 'ducks/server';
import { fetchReviews } from 'ducks/universal/reviews';
import { withSSR } from 'withSSR';
import { getI18nServerSideProps } from 'i18n';
import { detectLocale } from 'i18n-server';
import { selectLanguageOptions } from 'ducks/server/settings';
import { languageOptionMap } from 'lib/util/i18nHelpers';
import { absoluteUrl } from 'lib/util/absoluetUrl';
import { findPMPLinkForLocale } from 'lib/util/findPMPLinkForLocale';

interface Props {
  apiKey: string;
  host: string;
  ogpPromotionId: string;
  showOgpHighlight: boolean;
}

const BookingWidgetProductDetailsPage: NextPage<Props> = ({
  apiKey,
  host,
  ogpPromotionId,
  showOgpHighlight,
}) => {
  const router = useRouter();
  const url = `${host}${router.asPath}`;
  return (
    <ApiKeyContext.Provider
      value={{
        apiKey,
        useApiKeyInPaths: false,
      }}
    >
      <CurrentUrlContext.Provider
        value={{
          url,
        }}
      >
        <Head>
          <meta property="og:url" content={url} />
          <meta property="og:type" content={'article'} />
          <meta name="twitter:card" content={'summary_large_image'} />
        </Head>
        <BookingWidgetLayout>
          <BookingWidgetProductDetails
            ogpPromotionId={ogpPromotionId}
            showOgpHighlight={showOgpHighlight}
          />
        </BookingWidgetLayout>
      </CurrentUrlContext.Provider>
    </ApiKeyContext.Provider>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  async ({ params, req, res, store }) => {
    const hostname = req.headers?.host?.split(':')[0] || '';
    const { apiKey } = await fetchConfigForHostname(hostname);

    const productId = params?.id as string;

    let locale = await detectLocale(req, res);

    // Fetch settings using detected language
    const dispatch: Dispatch<any> = store.dispatch;
    await dispatch(fetchSettingsServerSide(apiKey, params?.id as string, locale));

    // Verify that the detected language is in the supplier's supported languages. If it isn't,
    // default to the first supported language and refetch settings.
    const langOptions = selectLanguageOptions(store.getState());
    if (langOptions.length > 0 && !langOptions.some((langOpt) => langOpt.iso === locale)) {
      locale = langOptions[0].iso;
      await dispatch(fetchSettingsServerSide(apiKey, params?.id as string, locale));
    }

    // If there are private marketplaces but we are viewing as booking widget, redirect to a private marketplace.
    const pmpEnabled = !!store.getState().server.settings.all.private_marketplace;
    const pmpLinks = store.getState().server.settings.all.private_marketplace_links;
    if (!pmpEnabled && (pmpLinks ?? []).length > 0) {
      const pmpLink = findPMPLinkForLocale(pmpLinks, locale);
      if (pmpLink) {
        return {
          redirect: {
            destination: `${pmpLink.url}/products/${productId}`,
            permanent: false,
          },
        };
      }
    }

    await Promise.all([
      dispatch(fetchProductServerSide(apiKey, productId, locale)),
      dispatch(
        fetchReviews(apiKey, locale, {
          productIds: [productId],
          pageSize: 5,
        })
      ),
      dispatch(fetchCustomPagesServerSide(apiKey, locale)),
    ]);

    const product = store.getState().server.product.product;
    if (
      product?.booking_widget_settings?.blacklisted_languages?.some(
        (language) => languageOptionMap[language as keyof typeof languageOptionMap]?.iso === locale
      )
    ) {
      return {
        redirect: {
          destination: '/',
          permanent: false,
        },
      };
    }

    const i18nProps = await getI18nServerSideProps(locale);

    if (langOptions.length === 1) {
      (req as any).locale = langOptions[0].iso;
    } else {
      const sourceLanguage = product?.source_language || 'EN_US';
      (req as any).locale = languageOptionMap[sourceLanguage]?.iso;
    }

    const queries = url.parse(req.url || '', true).query;
    const ogpPromotionId = queries?.promo && typeof queries.promo === 'string' ? queries.promo : '';
    const showOgpHighlight = typeof queries?.highlight === 'string';

    return {
      props: {
        apiKey,
        host: absoluteUrl(req),
        ogpPromotionId,
        showOgpHighlight,
        ...i18nProps,
      },
    };
  }
);

export default withSSR()(BookingWidgetProductDetailsPage);
